import { Helmet, HelmetProvider } from "react-helmet-async";

const PrivacyPolicy = () => {
  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Privacy policy - Aronium</title>
          <meta name="description" content="Read Aronium's privacy policy and understand how Aronium handles your personal data" />
        </Helmet>
      </HelmetProvider>

      <section className="py-8">
        <div className="max-w-screen-xl mx-auto text-center max-w-screen">
          <h1 className="text-4xl mb-4">Privacy Policy</h1>
          <p>Personal data handling</p>
        </div>
      </section>

      <section>
        <div className="max-w-screen-xl mx-auto px-4 text-gray-800 prose">
          <p className="mb-4">
            We value your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, and protect your information when you visit our website.
          </p>

          <h2 className="text-xl font-semibold mt-8 mb-2">1. Data We Collect</h2>
          <p className="mb-4">When you use our website, we may collect the following types of data:</p>
          <ul className="list-disc list-inside mb-4">
            <li>Usage data (pages visited, time spent, browser and device information)</li>
            <li>IP address and approximate location (used for language or content customization)</li>
            <li>Interaction data related to forms, buttons, and navigation</li>
          </ul>
          <p className="mb-4">We do not collect sensitive personal information unless explicitly provided by you (e.g., via contact forms).</p>

          <h2 className="text-xl font-semibold mt-8 mb-2">2. How We Use Your Data</h2>
          <ul className="list-disc list-inside mb-4">
            <li>To understand and improve user experience on our website</li>
            <li>To analyze site traffic and performance</li>
            <li>To deliver relevant ads through third-party platforms (e.g., Facebook)</li>
            <li>To provide localized content based on your region</li>
          </ul>

          <h2 className="text-xl font-semibold mt-8 mb-2">3. Cookies and Tracking Technologies</h2>
          <p className="mb-4">We use cookies to support analytics, advertising, and basic functionality.</p>

          <h3 className="text-lg font-semibold mt-4 mb-1">a) Google Analytics</h3>
          <p className="mb-4">
            We use Google Analytics to understand how visitors interact with our website. Google may process anonymized data such as your IP address, device type, and behavior.
            <br />
            <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="text-blue-600 underline">Read Google’s privacy policy</a>
          </p>

          <h3 className="text-lg font-semibold mt-4 mb-1">b) Facebook Pixel</h3>
          <p className="mb-4">
            We use Facebook Pixel to measure the performance of our marketing campaigns and show relevant ads to you on Facebook.
            <br />
            <a href="https://www.facebook.com/privacy/policy" target="_blank" rel="noreferrer" className="text-blue-600 underline">Read Facebook’s privacy policy</a>
          </p>

          <h3 className="text-lg font-semibold mt-4 mb-1">c) Geolocation Services</h3>
          <p className="mb-4">
            We may use IP-based geolocation to provide localized content (such as language or currency). We do not store precise GPS location or share location data with third parties.
          </p>

          <h2 className="text-xl font-semibold mt-8 mb-2">4. Legal Basis for Processing (GDPR)</h2>
          <p className="mb-4">We collect and process data only when:</p>
          <ul className="list-disc list-inside mb-4">
            <li>You have given consent (e.g., via cookie banner)</li>
            <li>It is necessary for legitimate business purposes (e.g., fraud prevention, site security)</li>
            <li>We are legally obliged to do so</li>
          </ul>
          <p className="mb-4">You can revoke your consent at any time by clearing cookies or adjusting your browser preferences.</p>

          <h2 className="text-xl font-semibold mt-8 mb-2">5. Data Retention</h2>
          <p className="mb-4">
            We retain analytics and tracking data only for as long as needed to fulfill the purposes outlined in this policy. Data collected via cookies is subject to their respective expiration periods (usually between 1 day and 13 months).
          </p>

          <h2 className="text-xl font-semibold mt-8 mb-2">6. Your Rights</h2>
          <p className="mb-4">If you are a resident of the European Economic Area (EEA), you have the right to:</p>
          <ul className="list-disc list-inside mb-4">
            <li>Access, correct, or delete your personal data</li>
            <li>Withdraw consent for data processing</li>
            <li>File a complaint with a data protection authority</li>
          </ul>
          <p className="mb-4">For any requests regarding your data, please contact us via email.</p>

          <h2 className="text-xl font-semibold mt-8 mb-2">7. Manage Cookies via Your Browser</h2>
          <p className="mb-4">You can also manage or delete cookies through your browser settings. Use the links below to learn how to do this in your preferred browser:</p>
          <ul className="list-disc list-inside mb-6 text-blue-600">
            <li><a href="https://support.google.com/chrome/answer/95647?hl=en" target="_blank" rel="noreferrer" className="underline">Chrome</a></li>
            <li><a href="https://support.microsoft.com/en-us/help/4468242/microsoft-edge-browsing-data-and-privacy" target="_blank" rel="noreferrer" className="underline">Edge</a></li>
            <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noreferrer" className="underline">Firefox</a></li>
            <li><a href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" target="_blank" rel="noreferrer" className="underline">Safari</a></li>
            <li><a href="https://help.opera.com/en/latest/web-preferences/#cookies" target="_blank" rel="noreferrer" className="underline">Opera</a></li>
          </ul>

          <h2 className="text-xl font-semibold mt-8 mb-2">8. Contact Us</h2>
          <p className="mb-1">If you have any questions or concerns about this Privacy Policy or your personal data, feel free to reach out:</p>
          <ul className="mt-2 text-gray-700">
            <li><strong>Email:</strong> <a href="mailto:office@aronium.com" className="text-blue-600 underline">office@aronium.com</a></li>
            <li><strong>Business name:</strong> Aronium GmbH</li>
          </ul>
        </div>
      </section>
    </>
  );
};

export default PrivacyPolicy;
